import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { accessApi } from "../services/accessApi";

const AccessContext = createContext(null);

export function AccessProvider({ children }) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setAccess(null);
        setError("");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const token = await firebaseUser.getIdToken();

        const res = await accessApi.getMyAccess(token);
        setAccess(res?.data?.data || null);
      } catch (err) {
        console.error("AccessProvider load error:", err);
        setError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load access."
        );
        setAccess(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const refreshAccess = async () => {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      setAccess(null);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const token = await firebaseUser.getIdToken();
      const res = await accessApi.getMyAccess(token);
      setAccess(res?.data?.data || null);
    } catch (err) {
      console.error("refreshAccess error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to refresh access."
      );
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      access,
      loading,
      error,
      refreshAccess,
    }),
    [access, loading, error]
  );

  return (
    <AccessContext.Provider value={value}>
      {children}
    </AccessContext.Provider>
  );
}

export function useAccess() {
  return useContext(AccessContext);
}